const { translateString } = window.BAO.Utils

class TagalysSearch extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
  }

  setupListeners () {
    super.setupListeners()

    if (!window?.onTagalysReady) return

    this._initialized = false

    this.currentPage = 0
    this.totalPages = 0
    this.itemsPerPage = 48

    this.isLoadingPage = false
    this.isRestoringFromUrl = false
    this.targetPageFromUrl = 1

    this.totalResultsCount = 0
    this.renderedResultsCount = 0

    this.handleInitialise = this.handleInitialise.bind(this)
    this.handleLoadMoreClick = this.handleLoadMoreClick.bind(this)
    this.updateLoadMoreButton = this.updateLoadMoreButton.bind(this)
    this.updatePaginationMeta = this.updatePaginationMeta.bind(this)
    this.renderProductsHtml = this.renderProductsHtml.bind(this)
    this.buildHeader = this.buildHeader.bind(this)
    this.handleFilterChange = this.handleFilterChange.bind(this)
    this.updateSelectedFilters = this.updateSelectedFilters.bind(this)
    this.buildSortSelect = this.buildSortSelect.bind(this)
    this.handleSortChange = this.handleSortChange.bind(this)
    this.handleActiveTagClick = this.handleActiveTagClick.bind(this)
    this.buildActiveFiltersFromApplied =
      this.buildActiveFiltersFromApplied.bind(this)

    this.listeners.add(
      this.els['load-more']?.element,
      'click',
      this.handleLoadMoreClick,
    )

    this.listeners.add(document, 'click', this.handleGlobalClick)

    window.onTagalysReady(this.handleInitialise)

    this.externalEls.filters.elements.forEach((filtersEl) => {
      this.listeners.add(filtersEl, 'change', this.handleFilterChange)
    })

    this.listeners.add(document, 'AFTER_RANGE_SLIDER_CHANGE', (event) => {
      this.handleRangeFilterChange(event)
    })
  }

  handleInitialise () {
    if (this._initialized) return
    this._initialized = true

    const searchQuery = this.getSearchQueryFromUrl()
    const paginationContainer = this.els?.['pagination-load-more']?.element
    const productsContainer = this.els.products.element

    if (!searchQuery) {
      if (productsContainer) productsContainer.innerHTML = ''
      return
    }

    this.targetPageFromUrl = this.getPageFromUrl()
    this.isRestoringFromUrl = this.targetPageFromUrl > 1

    // eslint-disable-next-line no-undef
    window.searchResultsInstance = Tagalys.APIConnector.SearchResults.new({
      params: { query: searchQuery, perPage: this.itemsPerPage },
      onSuccess: async (response, helpers) => {
        const redirectFromHelpers =
          typeof helpers.getRedirectURL === 'function'
            ? helpers.getRedirectURL()
            : null

        const redirectFromMetadata = response?.metadata?.redirect_to_url
        const redirectUrl = redirectFromHelpers || redirectFromMetadata

        if (redirectUrl) {
          try {
            const resolvedRedirect = redirectUrl.startsWith('http')
              ? redirectUrl
              : new URL(
                  `${this.localePrefix}${redirectUrl}`,
                  window.location.origin,
              ).toString()

            const currentUrl =
              window.location.origin +
              window.location.pathname +
              window.location.search

            if (resolvedRedirect !== currentUrl) {
              window.location.href = resolvedRedirect
              return
            }
          } catch (error) {
            console.error(
              '[Tagalys] invalid redirect URL:',
              redirectUrl,
              error,
            )
          }
        }

        this.buildFilters(helpers)
        this.buildActiveFiltersFromApplied(helpers)

        const page = response?.page ?? 1
        const totalPages = response?.total_pages ?? 1
        const total = response?.total_count ?? 0
        const products = response?.products ?? []

        this.currentPage = page
        this.totalPages = totalPages
        this.totalResultsCount = total

        this.updateSelectedFilters(helpers)
        this.buildSortSelect(helpers)

        const clampedTarget = Math.min(
          Math.max(1, this.targetPageFromUrl),
          this.totalPages,
        )

        if (this.currentPage === 1) {
          this.buildHeader(response, helpers)
          const noResultsEl = this.els?.['no-results']?.element
          const gridEl = this.els?.grid?.element
          if (noResultsEl) noResultsEl.hidden = total > 0
          if (gridEl) gridEl.hidden = total === 0

          if (total === 0 && noResultsEl) {
            const recommendationsEl = noResultsEl.querySelector(
              'tagalys-recommendations',
            )
            if (recommendationsEl?.dataset.widgetId && window.onTagalysReady) {
              window.onTagalysReady(() => {
                recommendationsEl.fetchTagalysRecommendations()
              })
            }
          }
        }

        const shouldAppend = this.currentPage > 1
        const newCount = await this.renderProductsHtml(products, shouldAppend)

        if (paginationContainer) {
          paginationContainer.hidden = this.totalResultsCount === 0
        }

        this.renderedResultsCount = shouldAppend
          ? this.renderedResultsCount + newCount
          : newCount
        this.updatePaginationMeta()

        this.restoreFocus()

        if (this.isRestoringFromUrl) {
          if (this.currentPage < clampedTarget) {
            window.searchResultsInstance.helpers.goToNextPage()
            return
          }
          this.isRestoringFromUrl = false
          this.setPageInUrl(this.currentPage, { replace: true })
        } else {
          this.setPageInUrl(this.currentPage, { replace: true })
        }

        this.isLoadingPage = false
        this.updateLoadMoreButton()
      },
      onFailure: (response, helpers) => {
        console.error('[Tagalys] onFailure', { response, helpers })

        this.isLoadingPage = false
        this.isRestoringFromUrl = false
        this.updateLoadMoreButton()
      },
    })

    window.searchResultsInstance.helpers.call()
  }

  handleLoadMoreClick () {
    if (this.isLoadingPage) return
    if (this.currentPage >= this.totalPages) return

    this.isLoadingPage = true
    this.updateLoadMoreButton()

    window.searchResultsInstance.helpers.goToNextPage()
  }

  handleFilterChange (event) {
    const input = event.target

    if (!input) return
    if (input.type !== 'checkbox') return

    const groupId = input.getAttribute('data-filter-id')
    const value = input.getAttribute('data-filter-value')

    if (!groupId) return

    const helpers = window.searchResultsInstance.helpers
    if (!helpers) return

    input.disabled = true
    this.isLoadingPage = true
    this.updateLoadMoreButton()

    try {
      if (input.checked) {
        helpers.applyFilter(groupId, value)
        helpers.call({ page: 1 })
      } else if (!input.checked) {
        helpers.clearFilter(groupId, value)
        helpers.call({ page: 1 })
      }

      this.currentPage = 0
      this.totalPages = 0
      this.renderedResultsCount = 0
      this.isRestoringFromUrl = false
      this.targetPageFromUrl = 1
    } catch (event) {
      console.error('[Tagalys] filter toggle failed', event)
    } finally {
      input.disabled = false
    }
  }

  handleRangeFilterChange (event) {
    const sliderEl = event.detail?.slider

    if (!sliderEl) return

    const filterId = sliderEl.dataset.tagalysRangeFilter
    const minInput = sliderEl.querySelector('[data-range-slider-el="min"]')
    const maxInput = sliderEl.querySelector('[data-range-slider-el="max"]')

    if (!filterId || !minInput || !maxInput) return

    const min = parseFloat(minInput.value)
    const max = parseFloat(maxInput.value)

    this._appliedRangeFilters = this._appliedRangeFilters ?? {}
    this._appliedRangeFilters[filterId] = { min, max }

    const helpers = window.searchResultsInstance?.helpers
    if (!helpers) return

    this.isLoadingPage = true
    this.updateLoadMoreButton()

    helpers.clearFilter(filterId)
    helpers.applyFilter(filterId, { min, max })
    helpers.call({ page: 1 })

    this.currentPage = 0
    this.totalPages = 0
    this.renderedResultsCount = 0
    this.isRestoringFromUrl = false
    this.targetPageFromUrl = 1
  }

  handleSortChange (event) {
    const sortId = event.id
    const helpers = window.searchResultsInstance.helpers
    if (!helpers || !sortId) return

    this.isLoadingPage = true
    this.updateLoadMoreButton()

    if (helpers.applySortOption) {
      helpers.applySortOption(sortId)
      helpers.call({ page: 1 })
    } else {
      const params = {
        ...(window.searchResultsInstance.params || {}),
        sort: sortId,
        page: 1,
      }
      window.searchResultsInstance.params = params
      helpers.call(params)
    }
    this.currentPage = 0
    this.totalPages = 0
    this.renderedResultsCount = 0
    this.isRestoringFromUrl = false
    this.targetPageFromUrl = 1
  }

  updateLoadMoreButton () {
    const loadMoreButton = this.els?.['load-more']?.element
    if (!loadMoreButton) return

    const hasMorePages = this.currentPage < this.totalPages

    loadMoreButton.hidden = !hasMorePages
    loadMoreButton.disabled =
      this.isLoadingPage || this.isRestoringFromUrl || !hasMorePages

    const labelLoading = this.translations?.search?.pagination?.loading
    const labelMore = this.translations?.search?.pagination?.loadMore

    loadMoreButton.firstChild.innerText =
      this.isLoadingPage || this.isRestoringFromUrl ? labelLoading : labelMore
    if (this.isLoadingPage || this.isRestoringFromUrl) {
      loadMoreButton.setAttribute('aria-busy', 'true')
    } else {
      loadMoreButton.removeAttribute('aria-busy')
    }
  }

  updatePaginationMeta () {
    const paginationMetaElement = this.els?.['pagination-meta']?.element
    if (!paginationMetaElement) return

    const showingCount = this.renderedResultsCount
    const totalCount = this.totalResultsCount

    const template = this.translations?.search?.pagination?.resultsAmount

    paginationMetaElement.innerHTML = translateString(template, {
      count: String(showingCount),
      total: String(totalCount),
    })
  }

  buildHeader (response, helpers) {
    const headerTermElement = this.externalEls?.['header-term']?.element
    if (!headerTermElement) return

    const resultsTotal = response?.total_count ?? 0
    const resultsTerm = response?.query ?? this.getSearchQueryFromUrl()

    if (resultsTotal > 0) {
      if (!helpers.isValidQuery()) {
        const template =
          this.translations?.search?.resultsWithCount?.wrongSpell

        headerTermElement.innerHTML = translateString(template, {
          term: String(resultsTerm),
        })
      } else {
        const template =
          resultsTotal === 1
            ? this.translations?.search?.resultsWithCount?.one
            : this.translations?.search?.resultsWithCount?.other

        headerTermElement.innerHTML = translateString(template, {
          count: String(resultsTotal),
          terms: String(resultsTerm),
        })
      }
    } else {
      headerTermElement.innerHTML = translateString(
        this.translations?.quickSearch?.noResults,
        {
          terms: String(resultsTerm),
        },
      )
    }
  }

  async updateView (currentView, newView) {
    const listClass = `pg-Search`

    await window.BAO.Cart.updateCartAttributes({
      current_style: newView,
    })

    this.classList.remove(`${listClass}-${currentView}Filters`)
    this.classList.add(`${listClass}-${newView}Filters`)

    this.setAttribute(`data-filter-view`, newView)
  }

  buildFilters (helpers) {
    const filterGroups = helpers.getFilters()

    const renderShell = () =>
      (filterGroups || [])
        .map((filterGroup, index) => {
          if (filterGroup.type === 'range') {
            return this.buildRangeFilter(filterGroup)
          }

          const groupName = filterGroup.label
          const groupId = filterGroup.id

          return `
          <div class="drw-ProductGridFacets_Item">
            <button
              type="button"
              class="drw-ProductGridFacets_Trigger fz-16_140"
              aria-controls="facet-${groupName}"
              data-accordion-group-el="trigger"
              aria-expanded="false"
            >
              <span>
                ${groupName}
                
                <span class="clc-ProductGridFacet_Count" data-tagalys-search-external-el="facets-selected" data-name="${groupId}" data-index="CollectionFacets-${index}"></span>
              </span>

              <span class="drw-ProductGridFacets_TriggerIcon drw-ProductGridFacets_TriggerIcon-plus">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M13 11h5v2h-5v5h-2v-5H6v-2h5V6h2z"/>
                </svg>
              </span>

              <span class="drw-ProductGridFacets_TriggerIcon drw-ProductGridFacets_TriggerIcon-minus">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M18 13H6v-2h12z"/>
                </svg>
              </span>
            </button>

            <div
              class="drw-ProductGridFacets_Accordion"
              id="facet-${groupName}"
              data-collection-filters-external-el="facet"
              data-filter-group="${groupId}"
              aria-hidden="true"
              data-accordion-group-el="content"
              data-index="CollectionFacets-${index}"
            ></div>
          </div>
        `
        })
        .join('')

    this.externalEls?.filters?.elements.forEach((containerEl) => {
      if (!containerEl.dataset.initialized) {
        containerEl.innerHTML = renderShell()
        containerEl.dataset.initialized = 'true'
      }
    })

    this.updateFilterContents(filterGroups)
  }

  buildRangeFilter (filterGroup) {
    const { id, label, min, max } = filterGroup
    const symbol = window.Shopify?.currency?.symbol ?? '$'
    const format = (val) => `${symbol}${val}`

    return `
    <div class="drw-ProductGridFacets_Item">
      <button
        type="button"
        class="drw-ProductGridFacets_Trigger fz-16_140"
        aria-controls="facet-${label}"
        data-accordion-group-el="trigger"
        aria-expanded="false"
      >
        <span>${label}</span>

        <span class="drw-ProductGridFacets_TriggerIcon drw-ProductGridFacets_TriggerIcon-plus">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <path fill="currentColor" d="M13 11h5v2h-5v5h-2v-5H6v-2h5V6h2z"/>
          </svg>
        </span>

        <span class="drw-ProductGridFacets_TriggerIcon drw-ProductGridFacets_TriggerIcon-minus">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <path fill="currentColor" d="M18 13H6v-2h12z"/>
          </svg>
        </span>
      </button>

      <div
        class="drw-ProductGridFacets_Accordion"
        id="facet-${label}"
        aria-hidden="true"
        data-accordion-group-el="content"
      >
        <div class="drw-ProductGridFacets_AccordionContent">
          <range-slider
            class="clc-PriceRange"
            min="${min}"
            max="${max}"
            data-tagalys-range-filter="${id}"
          >
            <div class="clc-PriceRange_Fields">
              <div class="clc-PriceRange_Field">
                <label class="clc-PriceRange_Label clc-PriceRange_Label-min fz-12_150" for="tagalys-price-min">
                  ${this.translations?.search?.filters?.priceMin}
                </label>
                <input
                  class="clc-PriceRange_Input clc-PriceRange_Input-min"
                  id="tagalys-price-min"
                  type="text"
                  value="${format(min)}"
                  placeholder="${format(min)}"
                  data-range-slider-el="minFormatted"
                >
              </div>

              <div class="clc-PriceRange_Field">
                <label class="clc-PriceRange_Label clc-PriceRange_Label-max fz-12_150" for="tagalys-price-max">
                  ${this.translations?.search?.filters?.priceMax}
                </label>
                <input
                  class="clc-PriceRange_Input clc-PriceRange_Input-max"
                  id="tagalys-price-max"
                  type="text"
                  value="${format(max)}"
                  placeholder="${format(max)}"
                  data-range-slider-el="maxFormatted"
                >
              </div>
            </div>

            <div class="clc-PriceRange_Slider">
              <button
                class="clc-PriceRange_Dot clc-PriceRange_Dot-left"
                type="button"
                tabindex="-1"
                aria-hidden="true"
                data-range-slider-el="touchLeft"
              >
                <span class="clc-PriceRange_DotInner"></span>
              </button>

              <button
                class="clc-PriceRange_Dot clc-PriceRange_Dot-right"
                type="button"
                tabindex="-1"
                aria-hidden="true"
                data-range-slider-el="touchRight"
              >
                <span class="clc-PriceRange_DotInner"></span>
              </button>

              <div class="clc-PriceRange_Line" data-range-slider-el="line">
                <span class="clc-PriceRange_LineInner"></span>
                <span class="clc-PriceRange_LineHighlighter" data-range-slider-el="highlighter"></span>
              </div>
            </div>

            <input type="hidden" value="${min}" data-range-slider-el="min">
            <input type="hidden" value="${max}" data-range-slider-el="max">
          </range-slider>
        </div>
      </div>
    </div>
  `
  }

  updateFilterContents (filterGroups) {
    const renderItems = (filterGroup) => {
      return `
      <div class="drw-ProductGridFacets_AccordionContent">
        <div class="clc-ProductGridFacet">
          <ul class="clc-ProductGridFacet_Items">
            ${(filterGroup.items || [])
              .map((filterItem, index) => {
                return `
                  <li class="clc-ProductGridFacet_Item">
                    <label class="clc-ProductGridFacet_Label fz-14_140" for="CollectionFacets-${filterGroup.label}-${index}" aria-disabled="false">
                      <input
                        type="checkbox"
                        class="clc-ProductGridFacet_Checkbox util-ScreenReaderOnly"
                        name="${filterGroup.id}"
                      value="${filterItem.id}"
                      id="CollectionFacets-${filterGroup.label}-${index}"
                        data-filter-id="${filterGroup.id}"
                        data-filter-value="${filterItem.id}"
                        ${filterItem.selected ? 'checked' : ''}
                      >
                      ${filterItem.label}
                      
                      <span class="clc-ProductGridFacet_LabelNumber fz-12_150">(${filterItem.count})</span>
                    </label>
                  </li>
                `
              })
              .join('')}
          </ul>
        </div>
      </div>
    `
    }

    this.externalEls?.filters?.elements.forEach((containerEl) => {
      filterGroups.forEach((filterGroup) => {
        if (filterGroup.type === 'range') return

        const facetEl = containerEl.querySelector(
          `[data-filter-group="${filterGroup.id}"]`,
        )

        if (facetEl) {
          facetEl.innerHTML = renderItems(filterGroup)
        }
      })
    })
  }

  buildActiveFiltersFromApplied (helpers) {
    const containers = Array.from(
      document.querySelectorAll(
        '[data-tagalys-search-external-el="active-facets"]',
      ),
    )

    if (!containers.length) return

    const groups = helpers.getAppliedFilters() || []
    const active = []

    groups.forEach((group) => {
      const groupId = group?.id
      const groupName = group?.label ?? groupId

      if (group.type === 'range') {
        const tracked = this._appliedRangeFilters?.[groupId]
        const selectedMin = tracked?.min ?? group.selected_min
        const selectedMax = tracked?.max ?? group.selected_max

        if (selectedMin !== undefined || selectedMax !== undefined) {
          const symbol = window.Shopify?.currency?.symbol ?? '$'
          const format = (val) =>
            group.display_format
              .replace('{{currency_label}}', symbol)
              .replace('{{value}}', val)
          const label = `${format(selectedMin)} – ${format(selectedMax)}`
          active.push({
            groupId,
            groupName,
            value: 'range',
            label,
            isRange: true,
          })
        }
        return
      }

      const items = Array.isArray(group?.items) ? group.items : []
      items.forEach((item) => {
        if (item?.selected === undefined || item.selected) {
          const value = String(item?.id ?? item?.value ?? '')
          const label = item?.label ?? value
          if (groupId && value) {
            active.push({ groupId, groupName, value, label })
          }
        }
      })
    })

    const itemsHtml = active
      .map(
        ({ groupId, groupName, value, label }) => `
      <div class="drw-ProductGridFacets_ActiveFacet">
        <button
          type="button"
          class="drw-ProductGridFacets_ActiveLink fz-12_150"
          data-active-tag
          data-filter-id="${groupId}"
          data-filter-value="${value}"
          aria-label="Remove ${groupName}: ${label}"
        >
          ${label}

          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16">
            <path stroke="currentColor" stroke-width="1.5" d="m5.168 5.172 5.657 5.657M5.175 10.828l5.657-5.657"/>
          </svg>
        </button>
      </div>
    `,
      )
      .join('')

    const activeHtml = active.length
      ? `
      <p class="drw-ProductGridFacets_ActiveTitle fz-16_140">
            ${this.translations?.search?.filters?.applied}

            <button
            type="button"
            class="clc-ProductGridFacets_ResetLink btn-Text fz-14_140"
            data-active-tag-clear-all
          >
            ${this.translations?.search?.filters?.clearAll}
          </button>
          </p>

          <div
            class="drw-ProductGridFacets_ActiveFacets"
          >
            ${itemsHtml}
          </div>
      `
      : ''

    const html = active.length ? activeHtml : ''
    if (active.length > 0) {
      this.externalEls['active-facets'].element.dataset.empty = false
    } else {
      this.externalEls['active-facets'].element.dataset.empty = true
    }
    containers.forEach((containerEl) => {
      containerEl.innerHTML = html
    })
  }

  updateSelectedFilters (helpers) {
    const selectedFacets = this.externalEls['facets-selected']?.elements
    let totalSelected = 0

    if (selectedFacets) {
      if (helpers.getAppliedFilters().length > 0) {
        const appliedFilters = helpers.getAppliedFilters()

        selectedFacets.forEach((facetEl) => {
          const matchingFilter = appliedFilters.find(
            (filter) => filter.id === facetEl.dataset.name,
          )

          if (matchingFilter && Array.isArray(matchingFilter.items)) {
            facetEl.innerText = `(${matchingFilter.items.length})`
          } else {
            facetEl.innerText = ''
          }
        })

        appliedFilters.forEach((filter) => {
          if (Array.isArray(filter.items)) {
            totalSelected += filter.items.length
          } else if (filter.type === 'range') {
            const tracked = this._appliedRangeFilters?.[filter.id]
            if (tracked) totalSelected += 1
          }
        })

        this.els['filter-count'].elements.forEach((countEl) => {
          countEl.innerText = totalSelected > 0 ? ` (${totalSelected})` : ''
        })
      } else {
        selectedFacets.forEach((facetEl) => {
          facetEl.innerText = ''
        })

        this.els['filter-count'].elements.forEach((countEl) => {
          countEl.innerText = ''
        })
      }
    }
  }

  handleActiveTagClick (event) {
    const btn = event.target?.closest?.('[data-active-tag]')
    if (!btn) return

    const helpers = window.searchResultsInstance?.helpers
    if (!helpers) return

    this.isLoadingPage = true
    this.updateLoadMoreButton()

    try {
      if (btn.hasAttribute('data-active-tag')) {
        const groupId = btn.getAttribute('data-filter-id')
        const value = btn.getAttribute('data-filter-value')

        if (!groupId) return

        if (value === 'range') {
          helpers.clearFilter(groupId)
          delete this._appliedRangeFilters?.[groupId]

          const slider = document.querySelector(
            `[data-tagalys-range-filter="${groupId}"]`,
          )
          if (slider) {
            slider.minValue = slider.min
            slider.maxValue = slider.max
            slider.setTouchElPositions()
          }
        } else {
          helpers.clearFilter(groupId, value)
        }
        helpers.call({ page: 1 })
      } else if (btn.hasAttribute('data-active-tag-clear-all')) {
        helpers.clearAllFilters()
        helpers.call({ page: 1 })
      }

      this.currentPage = 0
      this.totalPages = 0
      this.renderedResultsCount = 0
      this.isRestoringFromUrl = false
      this.targetPageFromUrl = 1
    } catch (event) {
      console.error('[Tagalys] active tag action failed', event)
    }
  }

  handleDrawerClearClick () {
    const helpers = window.searchResultsInstance?.helpers
    if (!helpers) return

    this.isLoadingPage = true
    this.updateLoadMoreButton()

    try {
      helpers.clearAllFilters()
      helpers.call({ filters: {}, page: 1 })

      this._appliedRangeFilters = {}

      document
        .querySelectorAll('[data-tagalys-range-filter]')
        .forEach((slider) => {
          slider.minValue = slider.min
          slider.maxValue = slider.max
          slider.setTouchElPositions()
        })

      this.currentPage = 0
      this.totalPages = 0
      this.renderedResultsCount = 0
      this.isRestoringFromUrl = false
      this.targetPageFromUrl = 1
    } catch (event) {
      console.error('[Tagalys] clear-all failed', event)
    }
  }

  handleGlobalClick = (event) => {
    const target = event.target

    const resetBtn = target.closest('[data-active-tag-clear-all]')
    if (resetBtn) {
      event.preventDefault()
      this.handleDrawerClearClick()
      return
    }

    const tagBtn = target.closest('[data-active-tag]')
    if (
      tagBtn &&
      tagBtn.closest('[data-tagalys-search-external-el="active-facets"]')
    ) {
      event.preventDefault()
      this.handleActiveTagClick(event)
    }

    const sortBtn = target.closest(
      '[data-tagalys-search-external-el="sort-facet"]',
    )
    if (sortBtn) {
      this.handleSortChange(sortBtn)
    }
  }

  buildSortSelect (helpers) {
    const container = this.externalEls.filters?.element
    if (!container) return

    let sortWrapper = container.querySelector('[data-sort-container]')

    if (!sortWrapper) {
      sortWrapper = document.createElement('div')

      sortWrapper.setAttribute('data-sort-container', '')

      sortWrapper.innerHTML = `
      <div class="drw-ProductGridFacets_Item">
        <button
          type="button"
          class="drw-ProductGridFacets_Trigger fz-16_140"
          aria-controls="sortby-accordion"
          data-accordion-group-el="trigger"
          aria-expanded="false"
          data-index="Facets-Drawer-sort-trigger"
        >
          ${this.translations.search.filters.sortBy}

          <span class="drw-ProductGridFacets_TriggerIcon drw-ProductGridFacets_TriggerIcon-plus">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <path fill="currentColor" d="M13 11h5v2h-5v5h-2v-5H6v-2h5V6h2z"/>
          </svg>
        </span>

        <span class="drw-ProductGridFacets_TriggerIcon drw-ProductGridFacets_TriggerIcon-minus">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <path fill="currentColor" d="M18 13H6v-2h12z"/>
          </svg>
        </span>
        </button>

        <div
          class="drw-ProductGridFacets_Accordion"
          id="sortby-accordion"
          data-accordion-group-el="content"
          aria-hidden="true"
          data-index="Facets-Drawer-sort-content"
        >
          <div class="drw-ProductGridFacets_AccordionContent">
            <div class="clc-ProductGridFacet">
              <ul
                class="clc-ProductGridFacet_Items"
                data-sort-options
              ></ul>
            </div>
          </div>
        </div>
      </div>
    `

      container.appendChild(sortWrapper)
    }

    this.updateSortOptions(helpers)
  }

  updateSortOptions (helpers) {
    const container = this.externalEls.filters?.element
    if (!container) return

    const optionsEl = container.querySelector('[data-sort-options]')

    if (!optionsEl) return

    const options = helpers.getSortOptions() || []
    const applied = helpers.getAppliedSortOption() || null

    const buttonsHtml = options
      .map((option) => {
        const selected =
          option.selected || (applied && applied.id === option.id)

        return `
        <li class="clc-ProductGridFacet_Item">
          <label
            class="clc-ProductGridFacet_Label fz-14_140"
            for="${option.id}"
          >
            <input
              class="clc-ProductGridFacet_Checkbox util-ScreenReaderOnly"
              data-tagalys-search-external-el="sort-facet"
              type="radio"
              name="sort_by"
              value="${option.label}"
              id="${option.id}"
              ${selected ? 'checked' : ''}
            >

            ${option.label}
          </label>
        </li>
      `
      })
      .join('')

    if (optionsEl.innerHTML !== buttonsHtml) {
      optionsEl.innerHTML = buttonsHtml
    }
  }

  async renderProductsHtml (productsArray, appendInsteadOfReplace) {
    const productsContainerElement = this.els?.products?.element
    if (!productsContainerElement) return 0

    const htmlForNewCards = await this.createProductCardsHtml(productsArray)
    const newCount = (productsArray || []).length

    if (appendInsteadOfReplace) {
      productsContainerElement.insertAdjacentHTML('beforeend', htmlForNewCards)
    } else {
      productsContainerElement.innerHTML = htmlForNewCards
    }

    await this.updateRegiosPrices()

    window.yotpoWidgetsContainer?.initWidgets()

    return newCount
  }

  async createProductCardsHtml (productsArray) {
    const productCardHtmlList = await Promise.all(
      (productsArray || []).map(async (product) => {
        const productUrl = `${window.Shopify.routes.root}products/${product.handle}`
        try {
          const sectionHtml = await window.BAO.sectionRenderer.fetchSingle(
            'tagalys-product-card',
            productUrl,
          )
          const sectionDocument =
            window.BAO.sectionRenderer.convertToDOM(sectionHtml)
          const articleElement = sectionDocument.querySelector('article')

          return articleElement
            ? `<li class="prd-List_Item">${articleElement.outerHTML}</li>`
            : ''
        } catch (error) {
          this.handleError?.(error)
          return ''
        }
      }),
    )

    return productCardHtmlList.join('')
  }

  async updateRegiosPrices () {
    const productElements = this.els?.products?.element?.querySelectorAll(
      '[data-regios-dopp-generic-product-id]',
    )

    if (!productElements || productElements.length === 0) return

    const api = await this.getRegiosApi()

    if (!api) return

    const calculations = new Map()

    for (const productElement of productElements) {
      const dataElement = productElement.querySelector('[data-regios-product]')

      if (!dataElement) continue

      let product

      try {
        product = JSON.parse(dataElement.textContent)
      } catch (error) {
        console.warn(
          '[TagalysSearch] Could not parse Regios product data',
          error,
        )

        continue
      }

      if (!calculations.has(product.productId)) {
        calculations.set(
          product.productId,
          api.calculateDiscountedPrices(product),
        )
      }

      const priceContainer = productElement.querySelector(
        '.regios-dopp-generic-price-container',
      )
      const salePriceElement = priceContainer?.querySelector(
        '.regios-dopp-generic-price-item--sale',
      )
      const regularPriceElement = priceContainer?.querySelector(
        '.regios-dopp-generic-price-item--regular',
      )

      if (!priceContainer || !salePriceElement || !regularPriceElement) {
        continue
      }

      try {
        const { salePrice, regularPrice } = await calculations.get(
          product.productId,
        )

        const isDiscounted = salePrice.amount < regularPrice.amount

        this.updatePriceText(salePriceElement, salePrice.formatted)

        if (isDiscounted) {
          this.updatePriceText(regularPriceElement, regularPrice.formatted)

          regularPriceElement.style.removeProperty('display')

          priceContainer.classList.add('regios-dopp-generic-price--on-sale')
        } else {
          this.updatePriceText(salePriceElement, regularPrice.formatted)
          this.updatePriceText(regularPriceElement, '')

          regularPriceElement.style.display = 'none'

          priceContainer.classList.remove('regios-dopp-generic-price--on-sale')
        }
      } catch (error) {
        console.warn(
          `[TagalysSearch] Could not calculate Regios price for product ${product.productId}`,
          error,
        )
      }
    }
  }

  updatePriceText (element, value) {
    const screenReaderElement = element.querySelector('.util-ScreenReaderOnly')

    element.replaceChildren()

    if (screenReaderElement) {
      element.append(screenReaderElement)
    }

    element.append(document.createTextNode(value))
  }

  getRegiosApi () {
    if (window.RegiosDOPP?.api?.v0) {
      return Promise.resolve(window.RegiosDOPP.api.v0)
    }

    return new Promise((resolve) => {
      const timeout = window.setTimeout(() => {
        resolve(null)
      }, 5000)

      window.addEventListener(
        'regios-dopp:api-initialized',
        (event) => {
          window.clearTimeout(timeout)

          resolve(event.detail.api)
        },
        { once: true },
      )
    })
  }

  getSearchQueryFromUrl () {
    const params = new URLSearchParams(window.location.search)
    const raw = params.get('q') ?? params.get('query') ?? ''
    return raw.replace(/\+/g, ' ').trim()
  }

  getPageFromUrl () {
    const params = new URLSearchParams(window.location.search)
    const page = parseInt(params.get('page') || '1', 10)
    return Number.isFinite(page) && page > 0 ? page : 1
  }

  setPageInUrl (pageNumber, { replace = true } = {}) {
    const url = new URL(window.location.href)
    if (pageNumber === 1) url.searchParams.delete('page')
    else url.searchParams.set('page', String(pageNumber))

    const method = replace ? 'replaceState' : 'pushState'
    window.history[method](null, '', url.toString())
  }

  restoreFocus () {
    const restoreId = this.dataset.restoreFocusId
    if (!restoreId) return

    const el = this.querySelector(`#${CSS.escape(restoreId)}`)
    if (el) el.focus()

    delete this.dataset.restoreFocusId
  }

  get localePrefix () {
    const root = window.Shopify?.routes?.root ?? '/'
    return root === '/' ? '' : root.replace(/\/$/, '')
  }

  get translations () {
    return window.theme?.strings ?? {}
  }
}

if (!customElements.get('tagalys-search')) {
  customElements.define('tagalys-search', TagalysSearch)
}
